import api from "../../../../lib/api";
import { IAwbPricingPayload } from "../../../../types/dashboard/awbPricingInterface";
import { IRateEstimatePayload } from "../../../../types/dashboard/rateCalculatorInterface";
import {
  IBillingBulkRunSearch,
  IBillingExportPayload,
  IBillingListSearch,
  IBulkDeleteBilling,
  IEditBillingPayload,
  IEditBillingQcPayload,
  IGenerateBilling,
} from "../../../../types/master/billingInterface";

export const getRateEstimateApi = (payload: IRateEstimatePayload) => {
  return api.post("/billing/rate-estimate", payload);
};

export const getAwbPricingApi = (payload: IAwbPricingPayload) => {
  return api.post("/billing/awb-pricing", payload);
};

const buildBillingListParams = (data: IBillingListSearch) => {
  const params: Record<string, string | number> = {
    search: data.search ?? "",
    customer_id: data.customer_id ?? "",
    from_date: data.from_date ?? "",
    to_date: data.to_date ?? "",
    service_mode: data.service_mode ?? "",
    branch_id: data.branch_id ?? "",
  };

  return params;
};

export const getBillingListApi = async (data: IBillingListSearch) => {
  return await api.get(`/billing/${data.page}/${data?.pageLimit ?? 10}`, {
    params: buildBillingListParams(data),
  });
};

export const generateBillingApi = (payload: IGenerateBilling) => {
  return api.post("/billing/generate-bulk", payload);
};

export const getBillingDataByIdApi = async (id: number | string) => {
  return await api.get(`/billing/detail/${id}`);
};

/*
 * Without proof files this is the usual JSON update. With proof files picked for other charges it is sent as
 * multipart in the same PUT (expected backend contract, not built yet):
 *   payload               the update as a JSON string
 *   charge_proof_<index>  the file for additional_charges[index]; the backend stores it and sets that charge's proof_url
 */
export const editBillingDataByIdApi = async (payload: IEditBillingPayload) => {
  if (!payload.proofFiles?.length) {
    return await api.put(`/billing/${payload.id}`, payload.data);
  }
  const formData = new FormData();
  formData.append("payload", JSON.stringify(payload.data));
  payload.proofFiles.forEach(({ index, file }) => formData.append(`charge_proof_${index}`, file));
  return await api.put(`/billing/${payload.id}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
};

export const updateBillingQcApi = async (payload: IEditBillingQcPayload) => {
  return await api.put(`/billing/qc/${payload.id}`, payload.data);
};

export const exportBillingApi = (payload: IBillingExportPayload) => {
  return api.post("/billing/export", payload, {
    responseType: "blob",
  });
};

export const deleteBillingApi = async (id: number | string) => {
  return await api.delete(`/billing/${id}`);
};

export const deleteBulkBillingApi = (payload: IBulkDeleteBilling) => {
  return api.delete("/billing/bulk", { data: payload });
};

export const getBillingBulkRunsApi = async (data: IBillingBulkRunSearch) => {
  return await api.get(`/billing/bulk-runs/${data.page}/${data?.pageLimit ?? 10}`, {
    params: {
      operation: data.operation ?? "",
    },
  });
};

export const getBillingBulkRunByIdApi = async (id: number | string) => {
  return await api.get(`/billing/bulk-runs/${id}`);
};


/*
 * Link to view a saved charge proof:
 *   GET /billing/:id/charge-proof?proof_url=<path>  ->  { data: { charge_name, proof_url, signed_url } }
 */
export const getBillingChargeProofLinkApi = (billingId: number | string, proofUrl: string) => {
  return api.get(`/billing/${billingId}/charge-proof`, { params: { proof_url: proofUrl } });
};
